import type { ProductListingPage } from "@decocms/apps-commerce/types";
import { BreadcrumbJsonLd } from "@decocms/blocks/hooks";
import { sanitizeBreadcrumb } from "~/utils/constants";
import { buildCategoryItemList, withBrandSuffix } from "./plpJsonLd";
import { markPageNotFound } from "~/server/pageStatus";

export interface Props {
  jsonLD: ProductListingPage | null;
  /**
   * Força noindex independentemente do número de resultados — usado na
   * página de busca interna (`/s`), que não deve ser indexada mesmo
   * quando devolve produtos.
   */
  forceNoIndex?: boolean;
}

function lastCrumbName(page: ProductListingPage | null): string | undefined {
  const items = page?.breadcrumb?.itemListElement;
  if (!items?.length) return undefined;
  const last = items[items.length - 1] as { name?: string } | undefined;
  return last?.name || undefined;
}

// Rede de segurança independente da higiene do catálogo: cobre categorias
// renomeadas pra sinalizar "desativada" (sufixo "- INATIVO", nós de lixo
// como "(duplicado)", raízes numéricas órfãs) mas que continuam com
// `IsActive: true` na VTEX — protege mesmo se ninguém rodar a limpeza de
// catálogo a tempo, ou se o mesmo erro se repetir no futuro.
export function looksLikeDeadCategory(name: string | undefined): boolean {
  return Boolean(name && /inativo|duplicado|^\d+$/i.test(name.trim()));
}

export const loader = (props: Props, req?: Request) => {
  const page = props.jsonLD;
  // Mesma regra do SearchTitleAndResult pra mostrar "ESSA PÁGINA NÃO EXISTE":
  // listagem sem produtos que não é busca por texto (busca sem resultado é
  // uma resposta válida). Como a página de categoria `/*` pega qualquer URL,
  // é este o caminho de toda URL inexistente do site — responde 404 em vez
  // de um 200 vazio. Ver src/server/pageStatus.ts.
  const isTextSearch = Boolean(req && new URL(req.url).searchParams.has("q"));
  if (!props.forceNoIndex && !isTextSearch && !page?.products?.length) {
    markPageNotFound();
  }
  if (!page) return { ...props, noIndexing: true };

  const itemList = buildCategoryItemList(page);
  const records = page.pageInfo?.records;
  const name = lastCrumbName(page);
  let fallbackTitle = name ? `${name} | Daniela Tombini` : undefined;
  let fallbackDescription = name
    ? `Compre ${name} na Daniela Tombini: pijamas, lingerie, biquíni e fitness com entrega para todo o Brasil.`
    : undefined;

  // Fallback para busca por termo (full-text search, ex: `/s?q=pijama`)
  // Quando não há breadcrumb (ex: resultado de busca interna) e nem `page.seo?.title`
  if (!fallbackTitle && req) {
    const term = new URL(req.url).searchParams.get("q");
    if (term) {
      fallbackTitle = `Resultados para "${term}" | Daniela Tombini`;
      fallbackDescription = `Veja os resultados da busca por "${term}" na Daniela Tombini.`;
    }
  }

  return {
    ...props,
    // O componente (`SeoPLP` abaixo) só lê `jsonLD.breadcrumb` — o resto do
    // `ProductListingPage` bruto (todos os produtos da página, cada um com
    // `additionalProperty`/`gtin`/clusters completos da VTEX) não é usado
    // depois daqui (o ItemList compacto pro JSON-LD já foi montado acima) e,
    // como esta seção é registrada dentro do array `sections` (não no campo
    // `seo` de nível de página), suas props inteiras são serializadas no
    // payload de hidratação SSR — mantendo o `jsonLD` completo aqui inflava
    // esse payload em ~3MB numa categoria de 48 produtos (achado real,
    // 2026-09-13).
    jsonLD: { breadcrumb: page.breadcrumb } as ProductListingPage,
    // Array real (não `[]`) quando há itens: `extractSeoFromProps`
    // (@decocms/blocks) só considera "esta seção já emitiu jsonLDs" quando
    // o array tem `.length` — um array vazio é tratado como "nada emitido"
    // e cai no fallback `deriveCommerceSeoFromJsonLD`, que serializa a
    // `ProductListingPage` bruta (todos os produtos da página) inteira como
    // `<script type="application/ld+json">`. Populando com o ItemList
    // compacto já montado pra renderização, o fallback nunca dispara.
    jsonLDs: itemList ? [itemList] : [],
    title: withBrandSuffix(page.seo?.title || fallbackTitle),
    description: page.seo?.description || fallbackDescription,
    // `page.seo?.canonical` vem do wrapper `productListingPageLean.ts`
    // (o loader vendor da Intelligent Search nunca calcula canonical
    // pra categoria/busca) — sem isso aqui, o framework não emite
    // `<link rel="canonical">` (`extractSeoFromProps` só emite quando
    // a seção de SEO devolve esse campo).
    canonical: page.seo?.canonical,
    // Categoria sem produtos (a maioria INATIVO, achado da auditoria de
    // catálogo do CRM) some do índice automaticamente, sem depender de
    // desativação manual na VTEX.
    noIndexing: Boolean(props.forceNoIndex) || records === 0 ||
      looksLikeDeadCategory(name),
  };
};

export const eager = true;
export const sync = true;

export default function SeoPLP({ jsonLD }: Props) {
  if (!jsonLD?.breadcrumb?.itemListElement?.length) return null;

  // O ItemList vai pro <head> via `loader`'s `jsonLDs` (framework injeta a
  // partir de `seo.jsonLDs`) — renderizar de novo aqui duplicaria o mesmo
  // bloco no <body>.
  return <BreadcrumbJsonLd breadcrumb={sanitizeBreadcrumb(jsonLD.breadcrumb)} />;
}
